import React, { Fragment, useState, useEffect } from 'react';
import { string, shape, arrayOf, func } from 'prop-types';
import axios from 'axios';

import SelectDropdown from '../../react-components/select-dropdown';

import { constEvent } from '../../utils/constants';
import { dispatchGenericEvent } from '../../utils/dispatch-events';

const DefaultView = ({
  endpointAddList,
  labelExistingList,
  placeholderSelectLists,
  labelNewList,
  btnLabelSave,
  btnLabelCancel,
  placeholderNoExistingLists,
  newListCharacterLimit,
  newListCharacterLimitMessage,
  productId,
  dropdownOptions,
  cbUpdateLoadingState,
  cbUpdateSuccessState
}) => {
  const [isDropdownSuccess, setIsDropdownSuccess] = useState(null);
  const [isDropdownDisabled, setIsDropdownDisabled] = useState(false);
  const [dropdownErrorMessage, setDropdownErrorMessage] = useState(null);
  const [dropdownPlaceholder, setDropdownPlaceholder] = useState(
    placeholderSelectLists
  );
  const [optionsLength, setOptionsLength] = useState(0);
  const [optionValue, setOptionValue] = useState(null);

  const [isInputSuccess, setIsInputSuccess] = useState(null);
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [inputErrorMessage, setInputErrorMessage] = useState(null);
  const [inputValue, setInputValue] = useState('');

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  useEffect(() => {
    resetState();

    if (dropdownOptions) {
      const length = dropdownOptions?.length || 0;

      setOptionsLength(length);
      setDropdownPlaceholder(
        length >= 1 ? placeholderSelectLists : placeholderNoExistingLists
      );
      setIsDropdownDisabled(!length >= 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownOptions]);

  const inputMaxLength = parseInt(newListCharacterLimit, 10);

  const resetState = () => {
    setIsDropdownSuccess(null);
    setIsDropdownDisabled(false);
    setDropdownErrorMessage(null);
    setDropdownPlaceholder(placeholderSelectLists);
    setOptionsLength(0);
    setOptionValue(null);
    setIsInputSuccess(null);
    setIsInputDisabled(false);
    setInputErrorMessage(null);
    setInputValue('');
    setIsSubmitDisabled(true);
  }; //resetState

  const handleOnChangeDropdown = option => {
    setOptionValue(option);

    setIsDropdownSuccess(true);
    setDropdownErrorMessage(null);
    setIsInputDisabled(true);
    setIsSubmitDisabled(false);
  }; //handleOnChangeDropdown

  const handleOnChangeInput = e => {
    const value = e?.target?.value || '';
    const valueLength = value?.trim()?.length || 0;

    setInputValue(value);

    if (valueLength >= 1 && valueLength <= inputMaxLength) {
      setIsDropdownDisabled(true);
      setIsSubmitDisabled(false);
      setInputErrorMessage(null);
      setIsInputSuccess(true);
    } else if (valueLength > inputMaxLength) {
      setIsDropdownDisabled(true);
      setIsSubmitDisabled(true);
      setInputErrorMessage(newListCharacterLimitMessage);
      setIsInputSuccess(false);
    } else {
      if (optionsLength >= 1) setIsDropdownDisabled(false);
      setIsSubmitDisabled(true);
      setInputErrorMessage(null);
      setIsInputSuccess(null);
    }
  }; //handleOnChangeInput

  const handleAddToList = e => {
    e?.preventDefault();

    cbUpdateLoadingState(true);

    const data = {
      product_id: productId,
      existing_list_id: isDropdownDisabled ? null : optionValue?.value,
      new_list_name: isInputDisabled ? null : inputValue
    };

    axios({
      method: 'post',
      url: endpointAddList,
      data,
      headers: {
        'content-type': 'application/json'
      }
    })
      .then(response => {
        const { success, message } = response?.data || {};
        if (success) {
          cbUpdateSuccessState(true);
          dispatchGenericEvent(
            constEvent.ALLOW_OVERLAY_CLOSE_ADD_TO_LIST_MODAL
          );
        } else updateErrorMessages(message);
      })
      .catch(error => updateErrorMessages(error?.message))
      .finally(() => cbUpdateLoadingState(false));
  }; //handleAddToList

  const updateErrorMessages = message => {
    if (isDropdownDisabled === false) {
      setDropdownErrorMessage(message);
      setIsDropdownSuccess(false);
    }

    if (isInputDisabled === false) {
      setInputErrorMessage(message);
      setIsInputSuccess(false);
    }
  }; //updateErrorMessages

  const propsDropdown = {
    fieldLabel: (
      <Fragment>
        <svg>
          <use xlinkHref="#tag-lines" />
        </svg>
        <span>{labelExistingList}</span>
      </Fragment>
    ),
    isSuccess: isDropdownSuccess,
    isDisabled: isDropdownDisabled,
    placeholder: dropdownPlaceholder,
    options: dropdownOptions,
    value: optionValue,
    onChange: handleOnChangeDropdown
  }; //propsDropdown

  return (
    <Fragment>
      <div
        className={`modal-add-to-list__field-wrapper ${
          isDropdownDisabled ? 'modal-add-to-list__field-wrapper--disabled' : ''
        }`}
      >
        <SelectDropdown {...propsDropdown} />
        {dropdownErrorMessage ? (
          <span className="field__msg--error">{dropdownErrorMessage}</span>
        ) : null}
      </div>

      <div
        className={`modal-add-to-list__field-wrapper ${
          isInputDisabled ? 'modal-add-to-list__field-wrapper--disabled' : ''
        }`}
      >
        <div
          className={`field-wrapper ${
            isInputSuccess === true ? 'field-wrapper--success' : ''
          } ${isInputSuccess === false ? 'field-wrapper--error' : ''}`}
        >
          <label className="field__label">
            <svg>
              <use xlinkHref="#tag-plus" />
            </svg>
            <span>{labelNewList}</span>
          </label>
          <div className="field__input-wrapper">
            <input
              className="field__input"
              type="text"
              value={inputValue}
              disabled={isInputDisabled}
              onChange={handleOnChangeInput}
            />
          </div>
          {inputErrorMessage ? (
            <span className="field__msg--error">{inputErrorMessage}</span>
          ) : null}
        </div>
      </div>

      <div className="modal-add-to-list__btn-wrapper">
        <button
          type="button"
          className="btn btn--primary"
          disabled={isSubmitDisabled}
          onClick={handleAddToList}
        >
          {btnLabelSave}
        </button>
        <a
          href="#"
          className="link"
          data-generic-modal-close
          onClick={e => e.preventDefault()}
        >
          {btnLabelCancel}
        </a>
      </div>
    </Fragment>
  );
};

DefaultView.propTypes = {
  endpointAddList: string.isRequired,
  labelExistingList: string.isRequired,
  placeholderSelectLists: string.isRequired,
  labelNewList: string.isRequired,
  btnLabelSave: string.isRequired,
  btnLabelCancel: string.isRequired,
  placeholderNoExistingLists: string.isRequired,
  newListCharacterLimit: string.isRequired,
  newListCharacterLimitMessage: string.isRequired,
  productId: string.isRequired,
  dropdownOptions: arrayOf(
    shape({
      value: string.isRequired,
      label: string.isRequired
    })
  ),
  cbUpdateLoadingState: func.isRequired,
  cbUpdateSuccessState: func.isRequired
};

export default DefaultView;
